
























































import { Component, Mixins, Inject } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { IState } from "@/store";
import { ITaxTemplate, ITaxTag, ITaxBusinessType } from "@/models/tax";
import HasValidation from "@/mixins/HasValidation";
import CountriesStore from "@/mixins/constants/countriesStore";
import {
  ITaxTemplateProvider,
  ITaxTemplateProviderMethods
} from "@/models/providers/tax";

const initialForm = {
  id: "",
  name: "",
  tax_tags: [] as ITaxTag[],
  business_types: [] as ITaxBusinessType[],
  system_template: false
};

@Component({
  components: {
    "tax-tags-sortable-grid": () =>
      import("@/components/app/global/tax/tags/taxTagsSortableGrid.vue"),
    "tax-business-types-list": () =>
      import("@/components/app/global/tax/taxBussinesTypesList.vue")
  },
  computed: {
    ...mapState({
      taxTemplate(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule
        });
      }
    })
  }
})
export default class AddEditTaxTemplateForm extends Mixins(
  RequestPromises,
  HasValidation,
  CountriesStore
) {
  // Inject

  @Inject({ from: "taxTemplateProvider" })
  public taxTemplateProvider!: () => ITaxTemplateProvider;

  @Inject({ from: "taxTemplateProviderMethods" })
  public $taxTemplateMethods!: ITaxTemplateProviderMethods;

  // Data
  public form: ITaxTemplate = {} as ITaxTemplate;
  public showStickySave = true;
  public fields = ["name"];

  // Life-cycle methods

  public async created() {
    this.setForm();
  }

  // Getters

  public get $taxTemplateData() {
    return this.taxTemplateProvider();
  }

  public get taxTemplate() {
    return this.$taxTemplateData?.taxTemplate;
  }

  public get isFormDisabled() {
    return (
      this.$taxTemplateData.isProcessing ||
      !this.$taxTemplateData.canEdit ||
      this.$taxTemplateData.isSystemTemplate
    );
  }

  public get i18n() {
    return {
      title: this.$t("_.tax_template_name", { name: this.taxTemplate?.name }),
      manageTaxTags: this.$taxTemplateData.isSystemTemplate
        ? this.$t("_action.view_object", {
            object: this.$t("_.tags")
              .toString()
              .toLowerCase()
          })
        : this.$t("_action.select_object", {
            object: this.$t("_.tags")
              .toString()
              .toLowerCase()
          }),
      manageBusinessTypes: this.$t("_action.manage_object", {
        object: this.$t("_.business_types")
          .toString()
          .toLowerCase()
      }),
      modalCta: this.$t("_action.save")
    };
  }
  public get sanitizedForm() {
    return this.sanitizeForm(this.form);
  }

  // Methods

  public sanitizeForm(data) {
    return {
      ...this.$_.pick(data, this.fields),
      system_template: data.system_template ? 1 : 0,
      tax_tags: data.tax_tags?.map(tag => tag.id) ?? [],
      business_types: this.$_.sortBy(data.business_types?.map(i => i.id) ?? [])
    };
  }

  public setForm() {
    if (this.$taxTemplateData.isNewTemplate) this.form = { ...initialForm };
    else this.form = this.$_.cloneDeep(this.taxTemplate);

    this.form.system_template = Boolean(this.form.system_template);
  }

  public openManageTaxTagsModal() {
    this.$store.dispatch(`data/${DataModules.TAX_TAGS}/openManageModal`, {
      props: {
        selectedTaxTags: this.form?.tax_tags,
        editable:
          !this.$taxTemplateData.isSystemTemplate &&
          this.$taxTemplateData.canEdit
      },
      events: {
        success: ({ taxTags }) => {
          this.form.tax_tags = taxTags;
        }
      }
    });
  }

  public async onDeleteTaxTag(taxTag: ITaxTag) {
    this.showStickySave = false;
    this.form.tax_tags = this.form.tax_tags?.filter(i => i.id !== taxTag.id);
    await this.validateBeforeSubmit().finally(() => {
      this.showStickySave = true;
    });
  }

  public onDeleteBusinessType(businessType: ITaxBusinessType) {
    this.form.business_types = this.form.business_types?.filter(
      i => i.id !== businessType.id
    );
  }

  public revertChanges() {
    this.setForm();
  }

  public async validateBeforeSubmit() {
    try {
      this.$taxTemplateMethods.set("isProcessing", true);
      const result = await this.validate();
      if (result) {
        if (!this.$taxTemplateData.isNewTemplate)
          await this.$taxTemplateMethods.updateTaxTemplate(
            this.$taxTemplateData.taxTemplateId,
            this.sanitizedForm
          );
        else
          await this.$taxTemplateMethods.createTaxTemplate(this.sanitizedForm);

        this.$emit("close");
        this.$emit("success");
      } else {
        throw this.$t("_sentence.form_is_not_valid");
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$taxTemplateMethods.set("isProcessing", false);
    }
  }

  public onTaxTagUpdated(taxTag: ITaxTag) {
    const index = this.form.tax_tags?.findIndex(
      i => i.id === taxTag.id
    ) as number;

    if (index > -1) this.form.tax_tags?.splice(index, 1, taxTag);
  }
}
